import { useState, useEffect } from "react";

import { getRecommendedSlot } from "/imports/ui/components/booking";

/**
 * What a tap on a time does on the lessons page: book it (the cart and checkout), add it
 * to a pack being composed, propose it as a lesson's new time, or open the multi-option
 * picker — a bottom sheet on mobile, an inline expansion or a popover on desktop.
 */
export default function useLessonSlotPicking({
  slug,
  filters,
  weekStart,
  groupedSlots,
  pack,
  reschedule,
  allowTrainerPick,
  isMobile,
  isAuthenticated,
  addToCart,
  navigate,
  enqueueSnackbar,
  setSelectedSlot
}) {
  const [expandedTime, setExpandedTime] = useState(null);
  // Mobile-only UI state: which filter/option bottom sheet is open.
  const [activeSheet, setActiveSheet] = useState(null); // 'sport'|'service'|'trainer'|'duration'|'location'|'options'|null
  const [optionsAnchor, setOptionsAnchor] = useState(null); // multi-option popover anchor el
  const [optionsGroup, setOptionsGroup] = useState(null); // group whose options are shown
  const [optionsDate, setOptionsDate] = useState(null); // date the popover slots belong to

  const expandedGroup = groupedSlots.find((g) => g.time === expandedTime);

  // Reset the inline picker / sheets / popover when the underlying query changes.
  useEffect(() => {
    setExpandedTime(null);
    setActiveSheet(null);
    setOptionsAnchor(null);
    setOptionsGroup(null);
    setOptionsDate(null);
  }, [filters.date, filters.serviceId, filters.trainerId, filters.duration, filters.locationId, weekStart]);

  const closeOptionsPopover = () => {
    setOptionsAnchor(null);
    setOptionsGroup(null);
    setOptionsDate(null);
  };

  const closeSheet = () => {
    setActiveSheet(null);
    setExpandedTime(null);
  };

  const handleSlotSelect = (slot, date = filters.date) => {
    // Reschedule mode: a tap proposes the move, confirmed in a dialog — never the cart.
    if (reschedule.active) {
      reschedule.select(slot, date);
      closeOptionsPopover();
      closeSheet();
      return;
    }
    // Pack mode: taps compose the pack instead of booking one lesson.
    if (pack.active) {
      pack.togglePick(slot, date);
      closeOptionsPopover();
      closeSheet();
      return;
    }
    setSelectedSlot(slot);
    addToCart("lesson", {
      type: "lesson",
      date,
      time: slot.time,
      duration: slot.duration,
      trainerId: slot.trainerId,
      trainerName: slot.trainerName,
      locationName: slot.locationName,
      serviceId: slot.serviceId,
      // The lesson the client actually picked ("Hitting"), not the category it
      // belongs to. Checkout falls back to "Private Lesson" without it, which
      // reads as a different thing from the "Hitting" pack offered beside it.
      serviceName: slot.serviceName,
      pricingId: slot.pricingId,
      price: slot.price,
      currency: slot.currency,
      locationId: slot.locationId,
      // The court under a lesson is always resolved server-side (court picker) —
      // the client never chooses it, so it stays re-organizable.
      ...(slot.facilityId && { facilityId: slot.facilityId, facilityName: slot.facilityName, facilityAuto: true })
    });
    if (isAuthenticated) {
      navigate(`/${slug}/checkout`);
    } else {
      enqueueSnackbar("Please sign in to complete your booking", { variant: "warning" });
      navigate(`/${slug}/login?redirect=checkout`);
    }
  };

  // Trainer assigned by the club: the picker would offer a lone "Any Trainer"
  // row, so book the recommended slot outright. Several locations still need the
  // picker — the branch remains the client's choice.
  const autoBookedSlot = (group) => {
    if (allowTrainerPick) return null;
    // Several lengths at this time: the length is still the client's call.
    if ((group.durations || []).length > 1) return null;
    const locations = new Set((group.slots || []).map((s) => s.locationId).filter(Boolean));
    return locations.size > 1 ? null : getRecommendedSlot(group);
  };

  const handleGroupClick = (group) => {
    // Pack mode: a time that's already picked unpicks on tap, whichever
    // trainer row it came through.
    if (pack.active && pack.isPicked(filters.date, group.time)) {
      pack.togglePick(group.slots[0], filters.date);
      return;
    }
    if (group.slots.length === 1) {
      handleSlotSelect(group.slots[0]);
      return;
    }
    const auto = autoBookedSlot(group);
    if (auto) {
      handleSlotSelect(auto);
      return;
    }
    if (isMobile) {
      // On mobile the multi-option picker is a bottom sheet rather than an
      // inline expansion that pushes the rest of the page down.
      setExpandedTime(group.time);
      setActiveSheet("options");
    } else {
      setExpandedTime((cur) => (cur === group.time ? null : group.time));
    }
  };

  // Desktop week-grid chip click: single slot books immediately; multi opens a
  // popover anchored to the chip so the user can pick trainer/location.
  const handleWeekChipClick = (e, group, date) => {
    if (pack.active && pack.isPicked(date, group.time)) {
      pack.togglePick(group.slots[0], date);
      return;
    }
    if (group.slots.length === 1) {
      handleSlotSelect(group.slots[0], date);
      return;
    }
    const auto = autoBookedSlot(group);
    if (auto) {
      handleSlotSelect(auto, date);
      return;
    }
    setOptionsGroup(group);
    setOptionsDate(date);
    setOptionsAnchor(e.currentTarget);
  };

  return {
    expandedGroup,
    activeSheet,
    setActiveSheet,
    optionsAnchor,
    optionsGroup,
    optionsDate,
    handleSlotSelect,
    handleGroupClick,
    handleWeekChipClick,
    closeOptionsPopover,
    closeSheet
  };
}
